import usePagedCollection from '../../hooks/usePagedCollection';
import InfiniteScrollSentinel from '../common/InfiniteScrollSentinel';
import { useState, useEffect, useCallback, useRef } from 'react';
import { Loader, Input, Button, Icon, Dimmer } from 'semantic-ui-react';
import SusuModal from '../common/SusuModal';
import SusuToggle from '../common/SusuToggle';
import ManageLookbooksModal from '../manage-lookbooks-modal/ManageLookbooksModal';
import LookbookCard from './LookbookCard';
import LookbookEntryCard from './LookbookEntryCard';
import './LookbookPanelView.css';

import {
    useLookbookPageQuery,
    useLookbookEntryPageQuery,
    useLazyGetLookbookEntriesQuery,
    useCreateLookbookMutation,
    useDeleteLookbookMutation,
    useUpdateLookbookMutation
} from '../../redux/apis/endpoints/lookbookApi';
import { useVTOResults } from '../../hooks/useVTOResults';
import { useShare } from '../../context/ShareContext';
import SelectionActionBar from '../selection-action-bar/SelectionActionBar';
import { getEntryPreviewUrl } from '../../utils/lookbookEntryImage';

const LookbookPanelView = ({
    onLookbookSelect,
    onEntrySelect,
    panelPosition = 'right',
    onStateChange,
    onClose,
    viewMode: controlledViewMode // New prop for controlled mode
}) => {
    // View state: 'list' (all lookbooks) or 'detail' (single lookbook entries)
    const [internalViewMode, setInternalViewMode] = useState('detail'); // Start in detail mode showing default lookbook

    // Use controlled mode if provided, otherwise internal state
    const viewMode = controlledViewMode || internalViewMode;

    const [selectedLookbook, setSelectedLookbook] = useState(null); // null = default lookbook (My Lookbook)



    // Modal states for lookbooks
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [newLookbookName, setNewLookbookName] = useState('');
    const [newLookbookIsPublic, setNewLookbookIsPublic] = useState(false);
    const [editingLookbook, setEditingLookbook] = useState(null);
    const [editLookbookName, setEditLookbookName] = useState('');
    const [editLookbookIsPublic, setEditLookbookIsPublic] = useState(false);
    const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState(false);
    const [isManageLookbooksOpen, setIsManageLookbooksOpen] = useState(false);

    const [isBulkMode, setIsBulkMode] = useState(false);
    const [selectedItems, setSelectedItems] = useState([]);
    const [itemToDelete, setItemToDelete] = useState(null); // Track single item to delete
    const contentRef = useRef(null);


    // RTK Query hooks for lookbooks
    const folderPage = usePagedCollection(useLookbookPageQuery);
    const { items: lookbooks, isLoading: lookbooksLoading } = folderPage;
    const [createLookbook, { isLoading: creatingLookbook }] = useCreateLookbookMutation();
    const [deleteLookbook, { isLoading: deletingLookbook }] = useDeleteLookbookMutation();
    const [updateLookbook, { isLoading: updatingLookbook }] = useUpdateLookbookMutation();

    // Use the VTO results hook which properly transforms the data
    const { vtoResults, selectedVTOResultId, isDeleting: deletingEntry, deleteVTOResult } = useVTOResults();
    const { openShare } = useShare();
    const [getFullEntries] = useLazyGetLookbookEntriesQuery();

    // Sort lookbooks: default first, then by updated_at
    const sortedLookbooks = [...lookbooks].sort((a, b) => {
        const aIsDefault = a.isDefault || a.name === 'My Lookbook';
        const bIsDefault = b.isDefault || b.name === 'My Lookbook';
        if (aIsDefault) return -1;
        if (bIsDefault) return 1;
        return new Date(b.updatedAt || b.created_at) - new Date(a.updatedAt || a.created_at);
    });

    // Get the default lookbook
    const defaultLookbook = lookbooks.find(lb => lb.isDefault || lb.name === 'My Lookbook');

    const isLookbookDefault = (lb) => lb.isDefault || lb.name === 'My Lookbook';

    const entryPage = usePagedCollection(useLookbookEntryPageQuery,
        { id: selectedLookbook?.id || defaultLookbook?.id },
        { skip: viewMode !== 'detail' || !(selectedLookbook?.id || defaultLookbook?.id) });
    const allEntries = entryPage.items;

    const newestResultId = vtoResults[0]?.id;
    const previousNewest = useRef(newestResultId);
    const reloadEntries = entryPage.reload;
    useEffect(() => {
        if (newestResultId !== previousNewest.current) reloadEntries();
        previousNewest.current = newestResultId;
    }, [newestResultId, reloadEntries]);

    // Share the current bulk selection: whole lookbooks in list view, individual
    // looks in detail view. Multiple lookbooks can't share as one live folder,
    // so those collapse into a frozen selection of their looks.
    const handleShareSelection = useCallback(async () => {
        if (!selectedItems.length) return;

        if (viewMode === 'list') {
            const selectedLookbooks = lookbooks.filter(lb => selectedItems.includes(lb.id));
            if (selectedLookbooks.length === 1) {
                const lb = selectedLookbooks[0];
                openShare({
                    sourceType: 'lookbook',
                    lookbookId: lb.id,
                    title: lb.name,
                    itemCount: lb.itemCount,
                    previewImages: lb.previewImages || []
                });
                return;
            }

            let fullEntries;
            try {
                fullEntries = await Promise.all(selectedLookbooks.map(lb => getFullEntries(lb.id).unwrap()));
            } catch {
                window.alert('Couldn’t load the selected lookbooks. Please try again.');
                return;
            }
            const vtoResultIds = [...new Set(fullEntries.flat().map(entry => entry.id))];
            openShare({
                sourceType: 'selection',
                vtoResultIds,
                title: `${selectedLookbooks.length} lookbooks`,
                itemCount: vtoResultIds.length,
                previewImages: selectedLookbooks.flatMap(lb => lb.previewImages || []).slice(0, 6)
            });
            return;
        }

        const selectedEntries = allEntries.filter(entry => selectedItems.includes(entry.id));
        openShare({
            sourceType: 'selection',
            vtoResultIds: selectedEntries.map(e => e.id),
            itemCount: selectedEntries.length,
            previewImages: selectedEntries.map(getEntryPreviewUrl).filter(Boolean)
        });
    }, [viewMode, selectedItems, lookbooks, allEntries, openShare, getFullEntries]);

    const entries = allEntries;
    const visibleLookbooks = sortedLookbooks;
    const activePage = viewMode === 'list' || (!defaultLookbook && folderPage.isError) ? folderPage : entryPage;
    const pagination = <InfiniteScrollSentinel hasMore={activePage.hasMore} loading={activePage.isFetching}
        error={activePage.isError} onLoadMore={activePage.loadMore} onRetry={activePage.refetch}
        count={activePage.items.length} />;

    // Non-default lookbooks that can be deleted
    const deletableLookbooks = sortedLookbooks.filter(lb => !isLookbookDefault(lb));
    const allLookbooksSelected = deletableLookbooks.length > 0 &&
        selectedItems.length === deletableLookbooks.length;

    // For entries bulk mode
    const allEntriesSelected = allEntries.length > 0 && selectedItems.length === allEntries.length;

    // Current lookbook name for header
    const currentLookbookName = viewMode === 'list'
        ? 'All Lookbooks'
        : (selectedLookbook === null
            ? (defaultLookbook?.name || 'My Lookbook')
            : selectedLookbook.name);

    // Notify parent of state changes for header controls
    useEffect(() => {
        if (onStateChange) {
            onStateChange({
                viewMode,
                isBulkMode,
                selectedCount: selectedItems.length,
                totalCount: viewMode === 'list' ? deletableLookbooks.length : allEntries.length,
                allSelected: viewMode === 'list' ? (deletableLookbooks.length > 0 && selectedItems.length === deletableLookbooks.length) : allEntriesSelected,
                currentLookbookName,
                isDefaultLookbook: selectedLookbook === null,
                onNewLookbook: () => setCreateModalOpen(true),
                onBulkEdit: () => setIsBulkMode(true),
                onSelectAll: handleSelectAll,
                onDelete: () => setBulkDeleteConfirmOpen(true),
                onDone: handleExitBulkMode,
                onBack: handleBackToList
            });
        }
    }, [viewMode, isBulkMode, selectedItems.length, sortedLookbooks.length, allEntries.length, currentLookbookName]);

    // Handlers
    const handleCreateLookbook = async () => {
        if (!newLookbookName.trim()) return;
        try {
            await createLookbook({ name: newLookbookName.trim(), isPublic: newLookbookIsPublic }).unwrap();
            folderPage.reload();
            setNewLookbookName('');
            setNewLookbookIsPublic(false);
            setCreateModalOpen(false);
        } catch (error) {
            console.error('Failed to create lookbook:', error);
        }
    };

    const handleLookbookClick = (lookbook) => {
        if (isBulkMode) {
            if (lookbook.isDefault) return; // Cannot select default lookbook
            // Toggle selection in bulk mode
            setSelectedItems(prev =>
                prev.includes(lookbook.id)
                    ? prev.filter(id => id !== lookbook.id)
                    : [...prev, lookbook.id]
            );
        } else {
            // Check if we are in controlled mode
            if (controlledViewMode) {
                onLookbookSelect?.(lookbook.id, lookbook.name, lookbook.isDefault);
            } else {
                // Navigate to lookbook detail view internally
                setSelectedLookbook(lookbook.isDefault ? null : lookbook);
                setInternalViewMode('detail');
                setIsBulkMode(false);
                setSelectedItems([]);
                onLookbookSelect?.(lookbook.id, lookbook.name, lookbook.isDefault);
            }
        }
    };

    const handleEntryClick = (entry) => {
        if (isBulkMode) {
            // Toggle selection in bulk mode
            setSelectedItems(prev =>
                prev.includes(entry.id)
                    ? prev.filter(id => id !== entry.id)
                    : [...prev, entry.id]
            );
        } else {
            // Select entry (load onto canvas)
            onEntrySelect?.(entry);
        }
    };

    const handleSelectAll = () => {
        if (viewMode === 'list') {
            if (allLookbooksSelected) {
                setSelectedItems([]);
            } else {
                setSelectedItems(deletableLookbooks.map(lb => lb.id));
            }
        } else {
            if (allEntriesSelected) {
                setSelectedItems([]);
            } else {
                setSelectedItems(allEntries.map(e => e.id));
            }
        }
    };

    const handleDeleteConfirm = async () => {
        try {
            if (itemToDelete) {
                // Delete single item
                if (viewMode === 'list') {
                    await deleteLookbook(itemToDelete).unwrap();
                } else {
                    await deleteVTOResult(itemToDelete);
                }
                setItemToDelete(null);
            } else {
                // Bulk delete
                if (viewMode === 'list') {
                    // Delete selected lookbooks
                    await Promise.all(selectedItems.map(id => deleteLookbook(id).unwrap()));
                } else {
                    // Delete selected VTO results
                    await Promise.all(selectedItems.map(id => deleteVTOResult(id)));
                }

                // Clear selection and exit bulk mode
                setSelectedItems([]);
                setIsBulkMode(false);
            }

            folderPage.reload();
            entryPage.reload();
            setBulkDeleteConfirmOpen(false);
        } catch (error) {
            console.error('Failed to delete:', error);
        }
    };

    const handleSingleDelete = (item) => {
        setItemToDelete(item.id);
        setBulkDeleteConfirmOpen(true);
    };

    const handleExitBulkMode = () => {
        setIsBulkMode(false);
        setSelectedItems([]);
    };

    const handleBackToList = () => {
        setInternalViewMode('list');
        setSelectedLookbook(null);
        setIsBulkMode(false);
        setSelectedItems([]);
    };

    const handleEditLookbook = (lookbook) => {
        setEditingLookbook(lookbook);
        setEditLookbookName(lookbook.name);
        setEditLookbookIsPublic(!!lookbook.isSharedForTryOn);
    };

    const handleUpdateLookbookName = async () => {
        if (!editLookbookName.trim() || !editingLookbook) return;
        try {
            await updateLookbook({ id: editingLookbook.id, name: editLookbookName.trim(), isPublic: editLookbookIsPublic }).unwrap();
            folderPage.reload();
            setEditingLookbook(null);
            setEditLookbookName('');
            setEditLookbookIsPublic(false);
        } catch (error) {
            console.error('Failed to update lookbook:', error);
        }
    };



    const isHorizontal = panelPosition === 'bottom';
    const isLoading = lookbooksLoading || (viewMode === 'detail' && entryPage.isLoading);
    const isDeleting = viewMode === 'list' ? deletingLookbook : deletingEntry;

    return (
        <div className={`lookbook-panel-view ${isHorizontal ? 'horizontal-layout' : ''}`}>
            {/* Header with controls - only show in right panel */}
            {!isHorizontal && (
                <div className="lookbook-bulk-header">
                    {isBulkMode ? (
                        <>
                            <Button
                                basic
                                color="teal"
                                size="tiny"
                                onClick={handleSelectAll}
                            >
                                {(viewMode === 'list' ? allLookbooksSelected : allEntriesSelected) ? 'Unselect' : 'Select All'}
                            </Button>
                            <Button
                                color="teal"
                                size="tiny"
                                onClick={handleExitBulkMode}
                            >
                                Done
                            </Button>
                        </>
                    ) : (
                        <>
                            {viewMode === 'list' && (
                                <Button
                                    basic
                                    color="teal"
                                    size="tiny"
                                    onClick={() => setCreateModalOpen(true)}
                                    icon
                                >
                                    <Icon name="plus" /> New
                                </Button>
                            )}
                            {((viewMode === 'list' && lookbooks.length > 1) || (viewMode === 'detail' && entries.length > 0)) && (
                                <span
                                    onClick={() => setIsBulkMode(true)}
                                    style={{
                                        color: '#00b5ad',
                                        fontSize: '13px',
                                        fontWeight: 500,
                                        cursor: 'pointer',
                                        padding: '2px 4px',
                                        userSelect: 'none'
                                    }}
                                >
                                    Select
                                </span>
                            )}
                        </>
                    )}
                </div>
            )}

            {/* Content */}
            <div ref={contentRef} className={`lookbook-content ${isBulkMode && selectedItems.length > 0 ? 'bulk-mode-padding' : ''}`}>
                {isLoading ? (
                    <div className="loading-state">
                        <Loader active inline size="small" />
                    </div>
                ) : viewMode === 'list' ? (
                    /* All Lookbooks Grid */
                    <div className="lookbooks-grid">
                        {visibleLookbooks.map(lookbook => (
                            <LookbookCard
                                key={lookbook.id}
                                lookbook={lookbook}
                                onClick={() => handleLookbookClick(lookbook)}
                                onEdit={handleEditLookbook}
                                onDelete={handleSingleDelete}
                                isBulkMode={isBulkMode}
                                isSelected={selectedItems.includes(lookbook.id)}
                                isDefault={isLookbookDefault(lookbook)}
                                isHorizontal={isHorizontal}
                            />
                        ))}
                        {pagination}
                    </div>
                ) : (
                    /* Lookbook Entries Grid */
                    <div className="entries-grid">
                        {entries.length > 0 ? (
                            entries.map(entry => (
                                <LookbookEntryCard
                                    key={entry.id}
                                    entry={entry}
                                    onClick={() => handleEntryClick(entry)}
                                    onDelete={handleSingleDelete}
                                    isBulkMode={isBulkMode}
                                    isSelected={selectedItems.includes(entry.id)}
                                    isActive={selectedVTOResultId === entry.id}
                                    isHorizontal={isHorizontal}
                                />
                            ))
                        ) : (
                            <div className="empty-state">
                                <p>No looks yet</p>
                                <p className="empty-hint">Generate a VTO to see it here</p>
                            </div>
                        )}
                        {pagination}
                    </div>
                )}
            </div>

            {/* Create Lookbook Modal */}
            <SusuModal
                open={createModalOpen}
                onClose={() => setCreateModalOpen(false)}
                size="mini"
            >
                <SusuModal.Header>Create New Lookbook</SusuModal.Header>
                <SusuModal.Content>
                    <Input
                        fluid
                        placeholder="Lookbook name"
                        value={newLookbookName}
                        onChange={(e) => setNewLookbookName(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleCreateLookbook()}
                    />
                    <div style={{ marginTop: '1rem' }}>
                        <SusuToggle
                            checked={newLookbookIsPublic}
                            onChange={setNewLookbookIsPublic}
                            label="Share to Community"
                        />
                    </div>
                </SusuModal.Content>
                <SusuModal.Actions>
                    <Button onClick={() => setCreateModalOpen(false)}>Cancel</Button>
                    <Button
                        color="teal"
                        onClick={handleCreateLookbook}
                        loading={creatingLookbook}
                        disabled={!newLookbookName.trim()}
                    >
                        Create
                    </Button>
                </SusuModal.Actions>
            </SusuModal>

            {/* Delete Confirmation Modal */}
            <SusuModal
                open={bulkDeleteConfirmOpen}
                onClose={() => {
                    setBulkDeleteConfirmOpen(false);
                    setItemToDelete(null);
                }}
                size="mini"
            >
                <Dimmer active={isDeleting} inverted>
                    <Loader size="large">Deleting...</Loader>
                </Dimmer>
                <SusuModal.Header>
                    Delete {viewMode === 'list' ? 'Lookbook' : 'Look'}{(!itemToDelete && selectedItems.length > 1) ? 's' : ''}
                </SusuModal.Header>
                <SusuModal.Content>
                    <p>
                        Are you sure you want to delete {itemToDelete ? 'this' : selectedItems.length}{' '}
                        {viewMode === 'list' ? 'lookbook' : 'look'}
                        {(!itemToDelete && selectedItems.length > 1) ? 's' : ''}?
                    </p>
                    {viewMode === 'list' && (
                        <p style={{ color: '#666', fontSize: '12px' }}>
                            This will permanently delete the lookbook and its organization.
                        </p>
                    )}
                </SusuModal.Content>
                <SusuModal.Actions>
                    <Button onClick={() => {
                        setBulkDeleteConfirmOpen(false);
                        setItemToDelete(null);
                    }} disabled={isDeleting}>Cancel</Button>
                    <Button
                        color="red"
                        onClick={handleDeleteConfirm}
                        disabled={isDeleting}
                    >
                        Delete
                    </Button>
                </SusuModal.Actions>
            </SusuModal>



            {/* Rename Lookbook Modal */}
            <SusuModal
                open={!!editingLookbook}
                onClose={() => setEditingLookbook(null)}
                size="mini"
            >
                <SusuModal.Header>Rename Lookbook</SusuModal.Header>
                <SusuModal.Content>
                    <Input
                        fluid
                        placeholder="Lookbook name"
                        value={editLookbookName}
                        onChange={(e) => setEditLookbookName(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleUpdateLookbookName()}
                    />
                    <div style={{ marginTop: '1rem' }}>
                        <SusuToggle
                            checked={editLookbookIsPublic}
                            onChange={setEditLookbookIsPublic}
                            label="Share to Community"
                        />
                    </div>
                </SusuModal.Content>
                <SusuModal.Actions>
                    <Button onClick={() => setEditingLookbook(null)} disabled={updatingLookbook}>Cancel</Button>
                    <Button
                        color="teal"
                        onClick={handleUpdateLookbookName}
                        disabled={!editLookbookName.trim() || updatingLookbook}
                    >
                        Save
                    </Button>
                </SusuModal.Actions>
            </SusuModal>

            {/* Bottom Action Panel */}
            {isBulkMode && (
                <SelectionActionBar
                    count={selectedItems.length}
                    actions={[
                        ...(viewMode === 'detail'
                            ? [{
                                key: 'lookbook',
                                icon: 'folder open outline',
                                label: 'lookbook',
                                onClick: () => setIsManageLookbooksOpen(true)
                            }]
                            : []),
                        {
                            key: 'share',
                            icon: 'share alternate',
                            label: 'share',
                            onClick: handleShareSelection
                        }
                    ]}
                    destructive={{
                        icon: 'trash alternate outline',
                        label: 'delete',
                        onClick: () => setBulkDeleteConfirmOpen(true)
                    }}
                />
            )}

            {/* Manage Lookbooks Modal (Add VTO results to lookbooks) */}
            <ManageLookbooksModal
                open={isManageLookbooksOpen}
                onClose={() => setIsManageLookbooksOpen(false)}
                selectedResultIds={viewMode === 'detail' ? selectedItems : []}
                onSuccess={() => {
                    setIsManageLookbooksOpen(false);
                    setIsBulkMode(false);
                    setSelectedItems([]);
                }}
            />
        </div>
    );
};

export default LookbookPanelView;
